import React, { Component, Fragment } from 'react';
import Calendar from 'react-big-calendar';
import classnames from 'classnames';
import Loading from 'components/Loading/Loading';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  fetchTeacherAvailability,
  setTeacherAvailability
} from 'actions/Teacher/availabilityActions';
import TeacherAvailabilitySelector from 'selectors/Teacher/TeacherAvailabilitySelector';

import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './style.css';

Calendar.setLocalizer(Calendar.momentLocalizer(moment));

const CALENDAR_MODE = {
  ADD: 'calendar_add',
  REMOVE: 'calendar_remove'
};

const calculateAvailableTimestamp = (hours, minutes) => {
  return hours * 60 + minutes;
};

// TODO: I feel like we shouldn't have to write our own diffing stuff here, the objects
// should be immutable and update when there are any mutations ...but for now, I whipped
// something together
const availabilityChanged = (prev, current) => {
  if (prev.length !== current.length) {
    return true;
  }

  const byDayCur = {};
  for (const e of current) {
    if (!byDayCur[e.resource]) byDayCur[e.resource] = [];
    byDayCur[e.resource].push(e);
  }

  const byDayPrev = {};
  for (const e of prev) {
    if (!byDayPrev[e.resource]) byDayPrev[e.resource] = [];
    byDayPrev[e.resource].push(e);
  }

  const curHashes = {};
  Object.keys(byDayCur).forEach((k) => {
    const starts = byDayCur[k]
      .map(s => s.start)
      .sort()
      .join('/');
    const ends = byDayCur[k]
      .map(s => s.end)
      .sort()
      .join('/');
    curHashes[k] = `${starts}|${ends}`;
  });

  const prevHashes = {};
  Object.keys(byDayPrev).forEach((k) => {
    const starts = byDayPrev[k]
      .map(s => s.start)
      .sort()
      .join('/');
    const ends = byDayPrev[k]
      .map(s => s.end)
      .sort()
      .join('/');
    prevHashes[k] = `${starts}|${ends}`;
  });

  for (const k in curHashes) {
    if (curHashes[k] !== prevHashes[k]) return true;
  }

  for (const k in prevHashes) {
    if (prevHashes[k] !== curHashes[k]) return true;
  }

  return false;
};

const formats = {
  dayFormat: (date) => moment(date).format('ddd'),
  timeGutterFormat: date => moment(date).format('ha'),
  selectRangeFormat: () => '',
  eventTimeRangeFormat: ({ start, end }) => {
    const startTime =
      start.getMinutes() > 0 ? moment(start).format('h:mma') : moment(start).format('ha');
    const endTime = end.getMinutes() > 0 ? moment(end).format('h:mma') : moment(end).format('ha');
    return `${startTime} - ${endTime}`;
  }
};

class OngoingAvailability extends Component {
  constructor(props) {
    super(props);

    this.state = {
      events: this.props.availability || [],
      mode: CALENDAR_MODE.ADD
    };
  }

  componentDidMount() {
    this.props.fetchTeacherAvailability();
  }

  componentDidUpdate(prevProps) {
    if (availabilityChanged(prevProps.availability, this.props.availability)) {
      this.setState({
        events: [...this.props.availability]
      });
    }
  }

  handleClickCalendarMode = (mode) => {
    return () => {
      this.setState({ mode });
    };
  }

  handleEventResize = (type, { event, start, end }) => {
    const newEvents = this.state.events.map(e => (e.resource === event.resource ?
      { start, end, resource: e.resource } :
      e
    ));
    this.setState({ events: newEvents });
  }

  // TODO: I don't think this function ever gets called
  handleEventDrop = ({ event, start, end }) => {
    if (
      event.resource !==
      moment(start)
        .format('ddd')
        .toLowerCase()
    ) {
      // disable drag event to other days
      return;
    }

    if (
      event.resource !==
      moment(end)
        .format('ddd')
        .toLowerCase()
    ) {
      end = new Date(start.getFullYear(), start.getMonth(), start.getDate(), 23, 59);
    }

    const newEvents = this.state.events.map(e => (e.resource === event.resource ?
      { start, end, resource: e.resource } :
      e
    ));
    this.setState({ events: newEvents });
  }

  handleSelectSlot = ({ start, end }) => {
    const { events, mode } = this.state;
    const resource = moment(start)
      .format('ddd')
      .toLowerCase();

    if (mode === CALENDAR_MODE.ADD) {
      const intersectingEvents = events.filter(
        // End of new event intersects with start of previous event
        e => (start.getTime() < e.start.getTime() && end.getTime() >= e.start.getTime()) ||
          // Start of new event intersects with end of previous event
          (end.getTime() > e.end.getTime() && start.getTime() <= e.end.getTime()) ||
          // New event is inside a previous event
          (start.getTime() >= e.start.getTime() && end.getTime() <= e.end.getTime())
      );
      if (intersectingEvents.length > 0) {
        // Start and end time of group of intersecting events is simply their min start and max end time
        const startTime = Math.min(
          start.getTime(),
          ...intersectingEvents.map(e => e.start.getTime())
        );
        const endTime = Math.max(end.getTime(), ...intersectingEvents.map(e => e.end.getTime()));
        const newEvent = { start: new Date(startTime), end: new Date(endTime), resource };
        const newEvents = [newEvent, ...events.filter(e => !intersectingEvents.includes(e))];
        this.setState({ events: newEvents });
      } else {
        this.setState({
          events: [
            ...events,
            { start: new Date(start.getTime()), end: new Date(end.getTime()), resource }
          ]
        });
      }
    } else {
      const intersectingEvents = events.filter(
        e => start.getTime() < e.end.getTime() && end.getTime() > e.start.getTime()
      );
      const newEvents = [...events.filter(e => !intersectingEvents.includes(e))];
      for (const e of intersectingEvents) {
        if (start.getTime() > e.start.getTime() && end.getTime() < e.end.getTime()) {
          // Remove interval is inside a block, so we split to two events
          newEvents.push({
            start: new Date(e.start.getTime()),
            end: new Date(start.getTime()),
            resource
          });
          newEvents.push({
            start: new Date(end.getTime()),
            end: new Date(e.end.getTime()),
            resource
          });
        } else if (start.getTime() <= e.start.getTime() && end.getTime() < e.end.getTime()) {
          // Remove interval starts before or when block starts and ends before block ends
          // So we cut off beginning of block
          newEvents.push({
            start: new Date(end.getTime()),
            end: new Date(e.end.getTime()),
            resource
          });
        } else if (start.getTime() > e.start.getTime()) {
          // Remove interval starts after block starts so we cut off the end of the block
          newEvents.push({
            start: new Date(e.start.getTime()),
            end: new Date(start.getTime()),
            resource
          });
        }
      }
      this.setState({ events: newEvents });
    }
  }

  handleClickSave = () => {
    const availability = this.state.events.map(e => {
      return {
        startTime: calculateAvailableTimestamp(e.start.getHours(), e.start.getMinutes()),
        endTime: calculateAvailableTimestamp(e.end.getHours(), e.end.getMinutes()),
        weekday: e.resource
      };
    });
    this.props.setTeacherAvailability(availability);
  }

  handleClickCancel = () => {
    this.setState({
      events: [...this.props.availability]
    });
  }

  render() {
    const { mode } = this.state;
    return (
      <Fragment>
        <div className="ongoing-availability px-4 px-lg-5">
          <div className="ongoing-availability__header row">
            <div className="col-12 col-lg-8">
              <p className="ongoing-availability__header-title">Ongoing Availability</p>
              <p className="ongoing-availability__header-desc">
                Drag and drop to add or remove available hours during your regular week.
              </p>
            </div>
            <div className="col-12 col-lg-4">
              <div className="d-flex align-items-center justify-content-start justify-content-lg-end">
                <div className="btn-group" role="group" aria-label="calendar-mode">
                  <button
                    type="button"
                    className={classnames(
                      'ongoing-availability__mode btn btn-secondary btn-cursor',
                      {
                        selected: mode === CALENDAR_MODE.ADD
                      }
                    )}
                    onClick={this.handleClickCalendarMode(CALENDAR_MODE.ADD)}
                  >
                    Add
                  </button>
                  <button
                    type="button"
                    className={classnames(
                      'ongoing-availability__mode btn btn-secondary btn-cursor',
                      {
                        selected: mode === CALENDAR_MODE.REMOVE
                      }
                    )}
                    onClick={this.handleClickCalendarMode(CALENDAR_MODE.REMOVE)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div
            className={classnames(
              'ongoing-availability__calendar',
              {
                'ongoing-availability__calendar--add': mode === CALENDAR_MODE.ADD
              },
              {
                'ongoing-availability__calendar--remove': mode === CALENDAR_MODE.REMOVE
              }
            )}
          >
            <Calendar
              selectable
              views={['week']}
              defaultView="week"
              events={this.state.events}
              onSelectSlot={this.handleSelectSlot}
              formats={formats}
              resizable
            />
          </div>
        </div>
        <div className="ongoing-availability__footer d-flex align-items-center px-4 px-lg-5 py-4">
          {this.props.setAvailabilityStatus === 'LOADING' ? (
            <Loading size="small" />
          ) : (
            <Fragment>
              <button
                type="button"
                className="btn btn-blue btn-cursor btn-lg btn-teacher-dashboard"
                onClick={this.handleClickSave}
                disabled={!availabilityChanged(this.state.events, this.props.availability)}
              >
                Save
              </button>
              <button
                type="button"
                className="btn btn-secondary btn-cursor btn-lg btn-teacher-dashboard ml-3"
                onClick={this.handleClickCancel}
              >
                Cancel
              </button>
            </Fragment>
          )}
        </div>
      </Fragment>
    );
  }
}

function mapStateToProps(state) {
  return {
    availability: TeacherAvailabilitySelector(state),
    availabilityStatus: state.teacher.availabilityStatus,
    setAvailabilityStatus: state.teacher.setAvailabilityStatus
  };
}

export default connect(
  mapStateToProps,
  {
    fetchTeacherAvailability,
    setTeacherAvailability
  }
)(OngoingAvailability);



// WEBPACK FOOTER //
// ./src/pages/teacher/Schedule/Availability/Ongoing/index.js